import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import TeamBadgeList from "../components/TeamBadgeList";
import MiniTable from "../components/MiniTable";
import AdmDashboard from "./administrasi/AdmDashboard";
import { ROLE_CLASS, ROLE_LABEL } from "../utils/roleLabels";

const MODULE_INFO = [
  { key: "inventory",  label: "Inventory"  },
  { key: "sekretaris", label: "Sekretaris" },
  { key: "bendahara",  label: "Bendahara"  },
];

const authFetch = async (url) => {
  const token = localStorage.getItem("accessToken");
  const res = await fetch(`${API_BASE_URL}${url}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json();
};

const toList = (data) => (Array.isArray(data) ? data : data?.results || []);

// [background, foreground] per transaction type, from the design's chip style.
const TX_TYPE = {
  IN:       { label: "Masuk",    colors: ["#5fd38d26", "#5fd38d"] },
  OUT:      { label: "Keluar",   colors: ["#ff8a7a26", "#ff8a7a"] },
  RETURN:   { label: "Kembali",  colors: ["#0f3a80",   "#bcd2ff"] },
  DAMAGED:  { label: "Rusak",    colors: ["#ffb45426", "#ffb454"] },
  REPAIR:   { label: "Diperbaiki", colors: ["#5fd38d26", "#5fd38d"] },
  TRANSFER: { label: "Transfer", colors: ["#b79cff26", "#cdbbff"] },
  // DB value is a misnomer: this is the write-off for barang that never
  // came back, not something temporary.
  TEMP_OUT: { label: "Terpakai", colors: ["#ffe94a26", "#ffe94a"] },
};

function TxChip({ type }) {
  const tp = TX_TYPE[type] || { label: type, colors: ["#0f3a80", "#bcd2ff"] };
  return (
    <span
      className="inline-block font-mono text-[10px] tracking-[0.08em] uppercase px-2 py-[3px] rounded"
      style={{ background: tp.colors[0], color: tp.colors[1] }}
    >
      {tp.label}
    </span>
  );
}

function NameCode({ name, code }) {
  return (
    <span className="inline-flex items-baseline gap-2.5 font-semibold">
      {name}
      {code && <span className="db-code">{code}</span>}
    </span>
  );
}

function SectionHead({ title, linkTo, linkLabel, note }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="eyebrow">{title}</div>
      {linkTo ? (
        <Link to={linkTo} className="db-link">
          {linkLabel || "Lihat semua →"}
        </Link>
      ) : (
        note && <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>{note}</div>
      )}
    </div>
  );
}

// A card that only exists while its list is non-empty (see "Perlu tindakan").
function ActionCard({ title, count, children }) {
  return (
    <div className="db-card">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[15px] font-semibold">{title}</span>
        <span className="tb-count is-alert">{count}</span>
      </div>
      {children}
    </div>
  );
}

function StatusTile({ group, title, count, note, to, tone, loading }) {
  const led = loading ? "is-off" : count > 0 ? tone : "";
  const body = (
    <>
      <div className="flex items-center justify-between">
        <span className="db-tile-group">{group}</span>
        <span className={`db-led ${led}`} aria-hidden="true" />
      </div>
      <div className="flex items-baseline gap-2.5">
        <span className="db-count">{loading ? "—" : count}</span>
        <span className="text-sm font-medium">{title}</span>
      </div>
      <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>
        {loading ? "Memuat…" : note}
      </div>
    </>
  );
  return to ? (
    <Link to={to} className="db-tile">
      {body}
    </Link>
  ) : (
    <div className="db-tile">{body}</div>
  );
}

// Superadmin-only "who's where" summary (hasil meet.txt: revisi Dashboard
// superadmin #1) — one card per module, admin-tier members with their
// kelompok color flags, then a short list of plain users in that module.
// Whole card is a Link to /roles per the client's explicit "kalo table ini
// di klik langsung link ke tab Role".
function ModuleCard({ title, admins, plainUsers }) {
  return (
    <Link to="/roles" className="db-card">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-base font-semibold">{title}</span>
        <span className="font-mono text-xs uppercase" style={{ color: "var(--text-secondary)" }}>
          {admins.length + plainUsers.length} orang
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <span className="db-label">ADMIN</span>
        {admins.length === 0 ? (
          <p className="text-[13px]" style={{ color: "var(--text-dim)" }}>Belum ada admin di modul ini.</p>
        ) : (
          admins.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold min-w-[56px]">{u.username}</span>
              <span className={`role-badge ${ROLE_CLASS[u.role] || "role-user"}`}>
                {ROLE_LABEL[u.role] || u.role}
              </span>
              <TeamBadgeList teams={u.teams || []} max={3} />
            </div>
          ))
        )}
      </div>
      {plainUsers.length > 0 && (
        <div className="flex flex-col gap-1.5 pt-3.5 mt-auto border-t border-[var(--border-subtle)]">
          <span className="db-label">USER · {plainUsers.length}</span>
          <span className="text-[13px] leading-normal" style={{ color: "var(--text-soft)" }}>
            {plainUsers.map((u) => u.username).join(", ")}
          </span>
        </div>
      )}
    </Link>
  );
}

export default function Dashboard() {
  const [role, setRole] = useState(null);
  const [ready, setReady] = useState(false);

  const [lowStock, setLowStock]   = useState(null);
  const [loanNotif, setLoanNotif] = useState(null);
  const [pendingTx, setPendingTx]     = useState([]);
  const [pendingBorrow, setPendingBorrow] = useState([]);
  const [pendingLoans, setPendingLoans]   = useState([]);
  const [pendingAsm, setPendingAsm]   = useState([]);
  const [highPriorityItems, setHighPriorityItems] = useState([]);
  const [recentTx, setRecentTx]      = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [suspOpen, setSuspOpen] = useState(false);

  const fetchAll = useCallback(async (userRole) => {
    const isAdm = userRole === "admin" || userRole === "superadmin";

    const tasks = [
      authFetch("/api/transactions/?page=1&page_size=8").then((d) => setRecentTx(toList(d))),
    ];
    if (isAdm) {
      tasks.push(
        authFetch("/api/items/low-stock-notifications/").then(setLowStock),
        authFetch("/api/loan-notifications/").then(setLoanNotif),
        authFetch("/api/transactions/pending/?page=1&page_size=20").then((d) => setPendingTx(toList(d))),
        authFetch("/api/admin/borrow-batches/pending/").then((d) => setPendingBorrow(toList(d))),
        authFetch("/api/loan-transactions/?status=PENDING").then((d) => setPendingLoans(toList(d))),
        authFetch("/api/panel-assemblies/?status=PENDING").then((d) => setPendingAsm(toList(d))),
        authFetch("/api/items/high-priority/").then((d) => setHighPriorityItems(d?.items || [])),
      );
    }
    if (userRole === "superadmin") {
      // Reuses /api/users/ (already carries role/teams/accessible_modules —
      // see inventory.teams.user_modules) rather than a new endpoint; Roles.jsx
      // already proves this exact aggregation works fine client-side.
      tasks.push(authFetch("/api/users/").then((d) => setAllUsers(toList(d))));
    }
    await Promise.all(tasks);
    setLoadingData(false);
  }, []);

  useEffect(() => {
    authFetch("/api/auth/me/").then((data) => {
      const r = data?.role ?? null;
      setRole(r);
      setReady(true);
      // Sekretaris/Bendahara don't have inventory access — skip the
      // inventory-only fetches entirely rather than firing 403s for data
      // they'll never see.
      if (r === "sekretaris" || r === "bendahara") {
        setLoadingData(false);
      } else {
        fetchAll(r);
      }
    }).catch(() => {
      setReady(true);
      setLoadingData(false);
    });
  }, [fetchAll]);

  if (!ready) {
    return <p className="text-sm text-muted p-4">Memuat...</p>;
  }

  const today = new Date()
    .toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "short", year: "numeric" })
    .toUpperCase();

  const pageHead = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="page-eyebrow">{today}</div>
        <h1 className="page-title">Dashboard</h1>
      </div>
      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Divisi Kelistrikan · Lisokom Inventory
      </div>
    </div>
  );

  if (role === "sekretaris" || role === "bendahara") {
    return (
      <div className="flex flex-col gap-9">
        {pageHead}
        <AdmDashboard primary={role} />
      </div>
    );
  }

  const isAdmin = role === "admin" || role === "superadmin";

  const loanAlerts = [...(loanNotif?.overdue || []), ...(loanNotif?.due_today || []), ...(loanNotif?.due_tomorrow || [])];

  // BorrowBatch (multi-item, per-event) and LoanTransaction (single-item,
  // due-date) are two different models under the hood, but to the person
  // reading the dashboard they're both just "barang yang lagi dipinjam" —
  // one combined pending queue instead of two same-looking cards.
  const pendingPeminjaman = [
    ...pendingBorrow.map((b) => ({
      kind: "batch",
      ref: b.reference_code || b.id,
      untuk: b.event_name || "-",
      oleh: b.user || "-",
      tanggal: b.created_at ? new Date(b.created_at).toLocaleDateString("id-ID") : "-",
    })),
    ...pendingLoans.map((l) => ({
      kind: "loan",
      ref: l.reference_code,
      untuk: l.item_name || l.item_code,
      oleh: l.pic_name || "-",
      tanggal: l.due_date || "-",
    })),
  ];

  // Only computed for superadmin (allUsers stays [] for everyone else, since
  // it's only fetched for that role) — module→role→kelompok breakdown,
  // "secara garis besarnya aja" per the client's own dashboard revision ask.
  const usersWithoutTeam = allUsers.filter(
    (u) => u.is_active && (u.role === "admin" || u.role === "user") && (u.teams || []).length === 0
  );
  const suspendedUsers = allUsers.filter((u) => !u.is_active);
  const moduleBuckets = MODULE_INFO.map((m) => ({
    ...m,
    admins: allUsers.filter(
      (u) => u.is_active && u.role !== "superadmin" && u.role !== "user"
        && (u.accessible_modules || []).includes(m.key)
    ),
    plainUsers: allUsers.filter(
      (u) => u.is_active && u.role === "user" && (u.teams || []).some((t) => t.module === m.key)
    ),
  }));

  const lowStockCount = lowStock?.total_count ?? 0;
  const loanAlertCount = loanNotif?.total_count ?? 0;
  const statusTiles = [
    {
      group: "PERINGATAN", title: "Stok menipis", count: lowStockCount, tone: "is-alert", to: "/items",
      note: lowStockCount ? "Komponen di bawah stok minimum" : "Semua stok komponen aman",
    },
    {
      group: "PERINGATAN", title: "Pinjaman jatuh tempo", count: loanAlertCount, tone: "is-warn", to: "/loan-transactions",
      note: loanAlertCount ? "Terlambat atau jatuh tempo segera" : "Tidak ada yang jatuh tempo",
    },
    {
      group: "PERSETUJUAN", title: "Transaksi", count: pendingTx.length, tone: "is-warn", to: "/transactions",
      note: pendingTx.length ? "Menunggu persetujuan" : "Tidak ada yang pending",
    },
    {
      group: "PERSETUJUAN", title: "Peminjaman", count: pendingPeminjaman.length, tone: "is-warn",
      to: pendingBorrow.length ? "/admin/borrow-batches/pending" : "/loan-transactions",
      note: pendingPeminjaman.length ? "Menunggu persetujuan" : "Tidak ada yang pending",
    },
    {
      group: "PERSETUJUAN", title: "Rakitan panel", count: pendingAsm.length, tone: "is-warn", to: "/admin/panel-assemblies",
      note: pendingAsm.length ? "Menunggu persetujuan" : "Tidak ada yang pending",
    },
  ];
  const allClear = statusTiles.every((t) => t.count === 0);
  const hasActionItems = !allClear;

  const suspendedPreview = suspendedUsers.length
    ? suspendedUsers.slice(0, 4).map((u) => u.username).join(", ")
      + (suspendedUsers.length > 4 ? `, +${suspendedUsers.length - 4} lainnya` : "")
    : "Tidak ada akun yang ditangguhkan.";

  return (
    <div className="flex flex-col gap-9">
      {pageHead}

      {/* ── STATUS SISTEM ── */}
      {isAdmin && (
        <section className="db-section">
          <SectionHead
            title="STATUS SISTEM"
            note={loadingData ? "Memuat…" : allClear ? "Semua aman" : "Ada yang perlu ditindak"}
          />
          <div className="db-tiles">
            {statusTiles.map((t) => (
              <StatusTile key={`${t.group}-${t.title}`} {...t} loading={loadingData} />
            ))}
          </div>
        </section>
      )}

      {/* ── PERLU TINDAKAN: rincian tiap tile yang ada isinya ── */}
      {isAdmin && !loadingData && hasActionItems && (
        <section className="db-section">
          <SectionHead title="PERLU TINDAKAN" />
          <div className="db-grid-wide">
            {lowStockCount > 0 && (
              <ActionCard title="Stok Menipis" count={lowStockCount}>
                <MiniTable
                  className="-mx-5 -mb-5"
                  cols={[
                    { key: "name",  label: "Barang" },
                    { key: "avail", label: "Ada",    right: true, shrink: true },
                    { key: "min",   label: "Min",    right: true, shrink: true, muted: true },
                    { key: "short", label: "Kurang", right: true, shrink: true },
                  ]}
                  rows={(lowStock?.items || []).map((item) => ({
                    name:  <NameCode name={item.name} code={item.code} />,
                    avail: item.quantity_available,
                    min:   item.minimum_stock,
                    short: <span className="font-bold" style={{ color: "var(--danger)" }}>-{item.shortage}</span>,
                  }))}
                />
              </ActionCard>
            )}

            {loanAlertCount > 0 && (
              <ActionCard title="Pinjaman Jatuh Tempo" count={loanAlertCount}>
                <MiniTable
                  className="-mx-5 -mb-5"
                  cols={[
                    { key: "ref",    label: "Ref",    shrink: true },
                    { key: "item",   label: "Barang" },
                    { key: "pic",    label: "PIC",    shrink: true, muted: true },
                    { key: "status", label: "Status", shrink: true },
                  ]}
                  rows={loanAlerts.map((loan) => {
                    const isOverdue   = loanNotif?.overdue?.some((x) => x.id === loan.id);
                    const isDueToday  = loanNotif?.due_today?.some((x) => x.id === loan.id);
                    return {
                      ref:    <span className="font-mono text-xs">{loan.reference_code}</span>,
                      item:   loan.item_name,
                      pic:    loan.pic_name,
                      status: isOverdue
                        ? <span className="font-semibold" style={{ color: "var(--danger)" }}>+{loan.days_overdue}h</span>
                        : isDueToday
                        ? <span style={{ color: "var(--amber)" }}>Hari ini</span>
                        : <span style={{ color: "var(--accent)" }}>Besok</span>,
                    };
                  })}
                />
              </ActionCard>
            )}

            {pendingTx.length > 0 && (
              <ActionCard title="Transaksi Pending" count={pendingTx.length}>
                <MiniTable
                  className="-mx-5 -mb-5"
                  cols={[
                    { key: "item", label: "Barang" },
                    { key: "type", label: "Tipe",  shrink: true },
                    { key: "qty",  label: "Qty",   right: true, shrink: true },
                    { key: "user", label: "User",  shrink: true, muted: true },
                  ]}
                  rows={pendingTx.slice(0, 6).map((tx) => ({
                    item: <NameCode name={tx.item?.name} code={tx.item?.code} />,
                    type: <TxChip type={tx.transaction_type} />,
                    qty:  tx.quantity,
                    user: tx.user,
                  }))}
                />
                {pendingTx.length > 6 && (
                  <Link to="/transactions" className="db-link text-center">
                    +{pendingTx.length - 6} lainnya →
                  </Link>
                )}
              </ActionCard>
            )}

            {pendingPeminjaman.length > 0 && (
              <ActionCard title="Peminjaman Barang Pending" count={pendingPeminjaman.length}>
                <MiniTable
                  className="-mx-5 -mb-5"
                  cols={[
                    { key: "ref",     label: "Ref",   shrink: true },
                    { key: "untuk",   label: "Untuk" },
                    { key: "oleh",    label: "Oleh",  shrink: true, muted: true },
                    { key: "tanggal", label: "Tgl",   shrink: true, muted: true },
                  ]}
                  rows={pendingPeminjaman.slice(0, 6).map((p) => ({
                    ref:     <span className="font-mono text-xs">{p.ref}</span>,
                    untuk:   p.untuk,
                    oleh:    p.oleh,
                    tanggal: p.tanggal,
                  }))}
                />
                {(() => {
                  const sisaBatch = pendingPeminjaman.slice(6).filter((p) => p.kind === "batch").length;
                  const sisaLoan = pendingPeminjaman.slice(6).filter((p) => p.kind === "loan").length;
                  return (
                    <>
                      {sisaBatch > 0 && (
                        <Link to="/admin/borrow-batches/pending" className="db-link text-center">
                          +{sisaBatch} lainnya →
                        </Link>
                      )}
                      {sisaLoan > 0 && (
                        <Link to="/loan-transactions" className="db-link text-center">
                          +{sisaLoan} lainnya →
                        </Link>
                      )}
                    </>
                  );
                })()}
              </ActionCard>
            )}

            {pendingAsm.length > 0 && (
              <ActionCard title="Rakitan Panel Pending" count={pendingAsm.length}>
                <MiniTable
                  className="-mx-5 -mb-5"
                  cols={[
                    { key: "ref",    label: "Ref",  shrink: true },
                    { key: "output", label: "Output" },
                    { key: "qty",    label: "Qty",  right: true, shrink: true },
                    { key: "user",   label: "User", shrink: true, muted: true },
                  ]}
                  rows={pendingAsm.slice(0, 6).map((asm) => ({
                    ref:    <span className="font-mono text-xs">{asm.reference_code}</span>,
                    output: <NameCode name={asm.output_item_name} code={asm.output_item_code} />,
                    qty:    asm.output_quantity,
                    user:   asm.user,
                  }))}
                />
                {pendingAsm.length > 6 && (
                  <Link to="/admin/panel-assemblies" className="db-link text-center">
                    +{pendingAsm.length - 6} lainnya →
                  </Link>
                )}
              </ActionCard>
            )}
          </div>
        </section>
      )}

      {/* ── MODUL & KELOMPOK (superadmin only) ── */}
      {role === "superadmin" && (
        <section className="db-section">
          <SectionHead title="MODUL & KELOMPOK" linkTo="/roles" linkLabel="Kelola kelompok →" />
          <div className="db-grid">
            {moduleBuckets.map((m) => (
              <ModuleCard key={m.key} title={m.label} admins={m.admins} plainUsers={m.plainUsers} />
            ))}
          </div>
          <div className="db-grid">
            <Link to="/roles" className="db-card !flex-row !items-center !justify-between">
              <div className="flex flex-col gap-1.5 min-w-0">
                <span className="text-[15px] font-semibold">Tanpa kelompok</span>
                <span className="text-[13px] cell-wrap" style={{ color: "var(--text-dim)" }}>
                  {usersWithoutTeam.length === 0
                    ? "Semua user aktif sudah punya kelompok."
                    : usersWithoutTeam.map((u) => u.username).join(", ")}
                </span>
              </div>
              <span
                className="text-[26px] font-bold"
                style={{ color: usersWithoutTeam.length ? "var(--amber)" : "var(--text-faint)" }}
              >
                {usersWithoutTeam.length}
              </span>
            </Link>

            <div className="db-card !gap-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1.5 min-w-0">
                  <span className="text-[15px] font-semibold">Suspended</span>
                  <span className="text-[13px] cell-wrap" style={{ color: "var(--text-dim)" }}>
                    {suspendedPreview}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className="text-[26px] font-bold"
                    style={{ color: suspendedUsers.length ? "var(--danger)" : "var(--text-faint)" }}
                  >
                    {suspendedUsers.length}
                  </span>
                  {suspendedUsers.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSuspOpen((v) => !v)}
                      aria-expanded={suspOpen}
                      className="sb-logout"
                    >
                      {suspOpen ? "TUTUP" : "LIHAT"}
                    </button>
                  )}
                </div>
              </div>
              {suspOpen && suspendedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-3 border-t border-[var(--border-subtle)]">
                  {suspendedUsers.map((u) => (
                    <span key={u.id} className="db-chip">{u.username}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── BARANG HIGH PRIORITY (admin/superadmin only — ditandai lewat
          checklist "High Priority" di modal edit barang, ItemsList.jsx) ── */}
      {isAdmin && (
        <section className="db-section">
          <SectionHead title="BARANG HIGH PRIORITY" linkTo="/items" />
          <div className="glass-table-wrap">
            {loadingData ? (
              <p className="db-empty">Memuat...</p>
            ) : highPriorityItems.length === 0 ? (
              <p className="db-empty">Belum ada barang yang ditandai high priority.</p>
            ) : (
              <MiniTable
                cols={[
                  { key: "item",  label: "Barang" },
                  { key: "cat",   label: "Kategori", shrink: true },
                  { key: "avail", label: "Tersedia", right: true, shrink: true },
                  { key: "min",   label: "Min",      right: true, shrink: true, muted: true },
                ]}
                rows={highPriorityItems.map((item) => ({
                  item:  <NameCode name={item.name} code={item.code} />,
                  cat:   item.category || "-",
                  avail: item.quantity_available > 0
                    ? <span className="font-bold tabular-nums" style={{ color: "var(--success)" }}>{item.quantity_available}</span>
                    : <span className="font-bold" style={{ color: "var(--danger)" }}>Kosong</span>,
                  min:   item.minimum_stock || "-",
                }))}
              />
            )}
          </div>
        </section>
      )}

      {/* ── AKTIVITAS TERBARU ── */}
      <section className="db-section">
        <SectionHead title="AKTIVITAS TERBARU" linkTo="/transactions" />
        <div className="glass-table-wrap">
          {loadingData ? (
            <p className="db-empty">Memuat...</p>
          ) : recentTx.length === 0 ? (
            <p className="db-empty">Belum ada transaksi.</p>
          ) : (
            <MiniTable
              cols={[
                { key: "item",  label: "Barang" },
                { key: "type",  label: "Tipe",   shrink: true },
                { key: "qty",   label: "Qty",    right: true, shrink: true },
                { key: "loc",   label: "Gudang", shrink: true },
                { key: "user",  label: "User",   shrink: true },
                { key: "time",  label: "Waktu",  right: true, shrink: true, mono: true, muted: true },
              ]}
              rows={recentTx.map((tx) => ({
                item: <NameCode name={tx.item?.name} code={tx.item?.code} />,
                type: <TxChip type={tx.transaction_type} />,
                qty:  <span className="tabular-nums">{tx.quantity}</span>,
                loc:  tx.location_name || "-",
                user: tx.user || "-",
                time: tx.created_at
                  ? new Date(tx.created_at)
                      .toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                      .toUpperCase()
                  : "-",
              }))}
            />
          )}
        </div>
      </section>
    </div>
  );
}
